/**
 * ClassificationPanel — affiche les suggestions IA classifier (Chunk 9.T.4).
 *
 * Monté conditionnellement dans `IntakeEventDetailPage.tsx` selon l'état de
 * `event.payload.classification` :
 *   - Absent (jamais classifié) → panel neutre + bouton « Analyser maintenant »
 *   - Analyse en cours (mutation isPending) → panel spinner + microcopy
 *   - Soft-fail (payload.classification.ok=false + reason) → panel red-tone
 *     + bouton « Analyser à nouveau » + explication reason bilingue
 *   - Success (payload.classification.ok=true) → panel green-tone + suggestions
 *     lisibles + evidence snippets courts safe
 *
 * Anti-leak strict (PO Q8 + sanitizer backend) :
 *   - Le backend strippe déjà `classifier_version` + `raw_llm_response` +
 *     `input_tokens` + `output_tokens` + `cost_usd` + `duration_ms` + `model`
 *     du wire response — ce composant NE peut PAS les afficher car ils ne
 *     sont pas dans son type d'entrée.
 *   - Aucun `console.log` de la classification.
 *
 * Réutilisation ClassificationPanel :
 *   - `IntakeEventDetailPage` — panel principal
 *   - `MaterializeEventDialog` — mode `readonly` pour rappeler au broker les
 *     suggestions au moment de la matérialisation (parité UX)
 */

import type { ClassificationPayload, ClassificationPayloadSuccess } from "@kollectivia/shared";
import { useTranslation } from "react-i18next";

type Props = {
  /** Payload classification sanitized (venant de `event.payload.classification`). */
  readonly classification: ClassificationPayload | null | undefined;
  /** Handler bouton « Analyser maintenant » / « Analyser à nouveau ». */
  readonly onTriggerClassify?: () => void;
  /** Mutation en vol (spinner + désactive bouton retry). */
  readonly isClassifying?: boolean;
  /**
   * Mode compact (utilisé dans MaterializeEventDialog pour rappel prefill).
   * Cache le bouton retry + microcopy réduite. Le panel reste visible mais
   * sans actions.
   */
  readonly compact?: boolean;
};

export function ClassificationPanel({
  classification,
  onTriggerClassify,
  isClassifying = false,
  compact = false,
}: Props) {
  const { t } = useTranslation();

  // État 1 — Analyse en cours (mutation en vol) — checké AVANT `!classification`
  // pour couvrir le cas classification==null + isClassifying==true (auto-trigger
  // depuis IntakeEventUploadPage : loading affiché AVANT que le premier
  // payload.classification apparaisse dans event.data).
  if (isClassifying) {
    return (
      <section
        role="status"
        aria-live="polite"
        aria-labelledby="cls-panel-title-loading"
        className="border-brand-200 bg-brand-50/60 rounded-lg border px-4 py-3"
      >
        <div className="flex items-center gap-3">
          <Spinner />
          <div>
            <h2 id="cls-panel-title-loading" className="text-sm font-semibold text-slate-800">
              {t("intake_events.detail.classification.loading.title")}
            </h2>
            <p className="mt-0.5 text-xs text-slate-600">
              {t("intake_events.detail.classification.loading.body")}
            </p>
          </div>
        </div>
      </section>
    );
  }

  // État 2 — Jamais classifié (idle)
  if (!classification) {
    if (compact) return null;
    return (
      <section
        aria-labelledby="cls-panel-title"
        className="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3"
      >
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 id="cls-panel-title" className="text-sm font-semibold text-slate-800">
              {t("intake_events.detail.classification.idle.title")}
            </h2>
            <p className="mt-0.5 text-xs text-slate-600">
              {t("intake_events.detail.classification.idle.body")}
            </p>
          </div>
          {onTriggerClassify && (
            <button
              type="button"
              onClick={onTriggerClassify}
              disabled={isClassifying}
              className="focus-visible:ring-brand-500 inline-flex touch-manipulation items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-800 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("intake_events.detail.classification.actions.analyze_now")}
            </button>
          )}
        </div>
      </section>
    );
  }

  // État 3 — Soft-fail
  if (classification.ok === false) {
    // 9.W.2 — un refus de configuration du fournisseur d'IA est DÉFINITIF :
    // proposer « Analyser à nouveau » enverrait l'utilisateur boucler pour rien.
    // On remplace la consigne et on retire l'action.
    const isProviderConfig = classification.reason === "provider_configuration";
    return (
      <section
        aria-labelledby="cls-panel-title-fail"
        className="rounded-lg border border-red-200 bg-red-50/60 px-4 py-3"
      >
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 id="cls-panel-title-fail" className="text-sm font-semibold text-red-900">
              {t("intake_events.detail.classification.soft_fail.title")}
            </h2>
            <p className="mt-0.5 text-xs text-red-800">
              {t(`intake_events.detail.classification.soft_fail.reasons.${classification.reason}`)}
            </p>
            <p className="mt-1 text-xs text-slate-600">
              {t(
                isProviderConfig
                  ? "intake_events.detail.classification.soft_fail.body_provider_configuration"
                  : "intake_events.detail.classification.soft_fail.body",
              )}
            </p>
          </div>
          {!compact && !isProviderConfig && onTriggerClassify && (
            <button
              type="button"
              onClick={onTriggerClassify}
              disabled={isClassifying}
              className="focus-visible:ring-brand-500 inline-flex touch-manipulation items-center justify-center rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-800 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("intake_events.detail.classification.actions.analyze_again")}
            </button>
          )}
        </div>
      </section>
    );
  }

  // État 4 — Success (ok === true)
  return <ClassificationSuccessPanel classification={classification} compact={compact} />;
}

// -----------------------------------------------------------------------------
// Success sub-panel — suggestions ranked (candidate_company + contact + evidence)
// -----------------------------------------------------------------------------

function ClassificationSuccessPanel({
  classification,
  compact,
}: {
  classification: ClassificationPayloadSuccess;
  compact: boolean;
}) {
  const { t } = useTranslation();
  const confidenceBucket = bucketFromConfidence(classification.confidence);

  return (
    <section
      aria-labelledby="cls-panel-title-success"
      className="rounded-lg border border-emerald-200 bg-emerald-50/50 px-4 py-3"
    >
      <div className="flex items-center justify-between gap-3">
        <h2 id="cls-panel-title-success" className="text-sm font-semibold text-emerald-900">
          {t("intake_events.detail.classification.success.title")}
        </h2>
        <ConfidenceBadge bucket={confidenceBucket} confidence={classification.confidence} />
      </div>

      <dl className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
        <Field
          label={t("intake_events.detail.classification.fields.kind")}
          value={classification.kind}
        />
        <Field
          label={t("intake_events.detail.classification.fields.lang")}
          value={classification.lang}
        />
      </dl>

      {classification.candidate_company && (
        <div className="mt-3 rounded-md border border-emerald-100 bg-white px-3 py-2">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {t("intake_events.detail.classification.candidate_company.title")}
          </p>
          <dl className="mt-1 grid grid-cols-1 gap-1 text-sm sm:grid-cols-2">
            {classification.candidate_company.legal_name && (
              <Field
                label={t("intake_events.detail.classification.candidate_company.legal_name")}
                value={classification.candidate_company.legal_name}
              />
            )}
            {classification.candidate_company.postal_code && (
              <Field
                label={t("intake_events.detail.classification.candidate_company.postal_code")}
                value={classification.candidate_company.postal_code}
              />
            )}
            {classification.candidate_company.business_number && (
              <Field
                label={t("intake_events.detail.classification.candidate_company.business_number")}
                value={classification.candidate_company.business_number}
              />
            )}
            {classification.candidate_company.identification_type && (
              <Field
                label={t(
                  "intake_events.detail.classification.candidate_company.identification_type",
                )}
                value={classification.candidate_company.identification_type}
              />
            )}
          </dl>
        </div>
      )}

      {classification.candidate_contact && (
        <div className="mt-3 rounded-md border border-emerald-100 bg-white px-3 py-2">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {t("intake_events.detail.classification.candidate_contact.title")}
          </p>
          <dl className="mt-1 grid grid-cols-1 gap-1 text-sm sm:grid-cols-2">
            {classification.candidate_contact.name && (
              <Field
                label={t("intake_events.detail.classification.candidate_contact.name")}
                value={classification.candidate_contact.name}
              />
            )}
            {classification.candidate_contact.email && (
              <Field
                label={t("intake_events.detail.classification.candidate_contact.email")}
                value={classification.candidate_contact.email}
              />
            )}
            {classification.candidate_contact.phone && (
              <Field
                label={t("intake_events.detail.classification.candidate_contact.phone")}
                value={classification.candidate_contact.phone}
              />
            )}
          </dl>
        </div>
      )}

      {!compact && classification.evidence.length > 0 && (
        <details className="mt-3 rounded-md border border-emerald-100 bg-white px-3 py-2 open:pb-3">
          <summary className="focus-visible:ring-brand-500 cursor-pointer touch-manipulation rounded text-xs font-medium text-slate-700 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1">
            {t("intake_events.detail.classification.evidence.title", {
              count: classification.evidence.length,
            })}
          </summary>
          <ul className="mt-2 space-y-1.5 text-xs text-slate-700">
            {classification.evidence.map((e, i) => (
              <li key={i} className="rounded bg-slate-50 px-2 py-1.5">
                <span className="font-mono text-slate-500">{e.field}</span>
                <span className="mx-1 text-slate-300">—</span>
                <span>{e.snippet}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="mt-0.5 truncate text-slate-900">{value}</dd>
    </div>
  );
}

function bucketFromConfidence(confidence: number): "very_likely" | "likely" | "possible" {
  if (confidence >= 0.9) return "very_likely";
  if (confidence >= 0.7) return "likely";
  return "possible";
}

function ConfidenceBadge({
  bucket,
  confidence,
}: {
  bucket: "very_likely" | "likely" | "possible";
  confidence: number;
}) {
  const { t } = useTranslation();
  const cls = {
    very_likely: "bg-emerald-100 text-emerald-800 ring-emerald-200",
    likely: "bg-slate-100 text-slate-700 ring-slate-200",
    possible: "bg-slate-50 text-slate-600 ring-slate-200",
  }[bucket];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`}
    >
      <span>{t(`intake_events.detail.classification.confidence.${bucket}`)}</span>
      <span aria-hidden="true" className="text-slate-400">
        ·
      </span>
      <span className="tabular-nums">{Math.round(confidence * 100)}%</span>
    </span>
  );
}

function Spinner() {
  return (
    <svg
      className="text-brand-600 h-4 w-4 animate-spin motion-reduce:animate-none"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.25" />
      <path d="M14 8a6 6 0 0 1-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
